/**
 * Copyright (c) 2025 Celestis AI
 *
 * WARNING: Unauthorized copying, modification, distribution, or use of this
 * software, via any medium, is strictly prohibited without explicit written
 * permission from Celestis AI.
 *
 * All rights reserved.
 */

import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import React, { Component, ErrorInfo, ReactNode, Suspense, lazy } from "react";
import "./index.css";
import { ConfigurationErrorScreen } from "./components/ConfigurationErrorScreen.tsx";

// Inject Smooth Circulars font
const fontFace = new FontFace(
  "Smooth Circulars",
  "url(/smooth-circulars.otf) format('opentype')"
);
fontFace.load().then((loadedFont) => {
  document.fonts.add(loadedFont);
}).catch((error) => {
  console.error("Failed to load Smooth Circulars font:", error);
});

import { getOptionalEnv } from '@/utils/env';
import { isProduction } from '@/config/environment';

// Initialize Sentry for error tracking
const sentryDsn = getOptionalEnv('VITE_SENTRY_DSN');
if (sentryDsn) {
  const environment = isProduction() ? 'production' : 'development';
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        // Display a user feedback widget for bug reports
        colorScheme: "system", // Matches user's system theme (light/dark)
        autoInject: false, // Don't auto-inject - we'll add it to the footer manually
        showBranding: false, // Hide Sentry branding for cleaner UI
        // Customize button text and labels
        submitButtonLabel: "Send Feedback",
        formTitle: "Report a Bug",
        messagePlaceholder: "What went wrong? Please describe the issue in detail...",
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: isProduction() ? 0.1 : 1.0, // Capture 100% in dev, 10% in production
    // Session Replay
    replaysSessionSampleRate: 0.1, // Sample 10% of sessions
    replaysOnErrorSampleRate: 1.0, // Sample 100% of sessions with errors
    // Add data like request headers and user info
    sendDefaultPii: true,
    environment,
  });
  console.log(`✅ Sentry initialized for ${environment} environment`);
  console.log(`🐛 User feedback widget enabled - users can report bugs`);
} else {
  console.warn("⚠️  Sentry DSN not configured - error tracking disabled");
}

// Capture unhandled promise rejections (async errors that never reach a try/catch).
// Sentry.captureException is a safe no-op if Sentry was not initialized.
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason ?? new Error('Unhandled promise rejection'));
});

// Error Boundary to catch configuration errors
class ConfigurationErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    // Check if this is a configuration error
    const isConfigError = error?.message?.includes('environment variable is not set') ||
                         error?.message?.includes('VITE_');
    
    if (isConfigError) {
      return { hasError: true, error };
    }
    // Re-throw non-configuration errors by returning null (let them bubble up)
    return { hasError: false, error: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isConfigError = error?.message?.includes('environment variable is not set') ||
                         error?.message?.includes('VITE_');
    if (isConfigError) {
      console.error('[ConfigurationErrorBoundary] Configuration error:', error);
      return;
    }
    // Non-config React render errors: report to Sentry so crashes are visible.
    console.error('[ConfigurationErrorBoundary] Unhandled React error:', error);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  render() {
    if (this.state.hasError) {
      return <ConfigurationErrorScreen errorMessage="The application is not properly configured. Please contact support for assistance." />;
    }

    return this.props.children;
  }
}

// Use lazy import to catch errors during module evaluation
const App = lazy(async () => {
  try {
    const appModule = await import("./App.tsx");
    return appModule;
  } catch (error: any) {
    // Check if this is a configuration error
    const isConfigError = error?.message?.includes('environment variable is not set') ||
                         error?.message?.includes('VITE_');
    
    if (isConfigError) {
      // Return error component instead
      return {
        default: () => <ConfigurationErrorScreen errorMessage="The application is not properly configured. Please contact support for assistance." />
      };
    }
    // Re-throw non-configuration errors
    throw error;
  }
});

// Wrap app in error boundary to catch configuration errors
createRoot(document.getElementById("root")!).render(
  <ConfigurationErrorBoundary>
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">Loading...</div>}>
      <App />
    </Suspense>
  </ConfigurationErrorBoundary>
);
